/**
 * Shared template → objectives mapping for People Leader allocation.
 *
 * Single source of truth used by BOTH the allocate dialog (useObjectiveAllocation)
 * and the team-level Template Library allocate flow (TemplateAllocationDialog).
 * Produces "wizard-format" objectives that allocateObjectivesToMember() then posts
 * to the canonical employee-objectives endpoint (status -> pending_approval).
 */

export type StrategicLookup = { id: number; code: string; name: string; display_name?: string }

export interface StrategicLookups {
  strategicObjectivesLookup?: StrategicLookup[]
  strategicInitiativesLookup?: StrategicLookup[]
  operationalInitiativesLookup?: StrategicLookup[]
}

export interface MappedObjectiveKPI {
  title: string
  description: string
  target: string
  unit: string
  frequency: string
}

export interface MappedObjective {
  title: string
  description: string
  kpa: string
  startDate?: string
  endDate?: string
  strategicObjectiveId?: number
  strategicInitiativeId?: number
  operationalInitiativeId?: number
  kpis: MappedObjectiveKPI[]
}

// KPA code/name → database id. Environment-stable (KPA ids are seeded constants).
const KPA_CODE_TO_ID: Record<string, number> = {
  'operating-financial': 1,
  'internal-business-processes': 2,
  'culture-people': 3,
  'customer-stakeholder': 4,
}

const KPA_NAME_TO_CODE: Record<string, string> = {
  'Operating and Financial Results': 'operating-financial',
  'Internal Business Processes and Enablement': 'internal-business-processes',
  'Culture and People': 'culture-people',
  'Customer and Stakeholder Relationships': 'customer-stakeholder',
}

/** Resolve a KPA database id from the many shapes producers emit (flat id/code/name or nested kpa{}). */
function resolveKpaId(obj: any): number | undefined {
  if (obj.kpa_id) return obj.kpa_id
  if (obj.kpa_code && KPA_CODE_TO_ID[obj.kpa_code]) return KPA_CODE_TO_ID[obj.kpa_code]
  if (obj.kpa_name && KPA_NAME_TO_CODE[obj.kpa_name]) return KPA_CODE_TO_ID[KPA_NAME_TO_CODE[obj.kpa_name]]
  // Nested kpa {} shape (manager wizard): kpa.id is a slug code, kpa.display_name is the full name
  const nested = obj.kpa
  if (nested) {
    if (typeof nested.id === 'number') return nested.id
    if (typeof nested.id === 'string' && KPA_CODE_TO_ID[nested.id]) return KPA_CODE_TO_ID[nested.id]
    if (nested.display_name && KPA_NAME_TO_CODE[nested.display_name]) return KPA_CODE_TO_ID[KPA_NAME_TO_CODE[nested.display_name]]
    if (nested.name && KPA_NAME_TO_CODE[nested.name]) return KPA_CODE_TO_ID[KPA_NAME_TO_CODE[nested.name]]
  }
  return undefined
}

function hasAnyKpaSignal(obj: any): boolean {
  return !!(obj.kpa_id || obj.kpa_code || obj.kpa_name || obj.kpa?.id || obj.kpa?.name || obj.kpa?.display_name)
}

export interface MapTemplateResult {
  objectives: MappedObjective[]
  error?: string
}

/**
 * Map a template's objectives_data.objectives into allocation-ready objectives.
 * Returns an `error` string (instead of throwing) when the template is empty or
 * has objectives without a resolvable KPA, so callers can toast and bail cleanly.
 */
export function mapTemplateToObjectives(template: any, lookups: StrategicLookups = {}): MapTemplateResult {
  const {
    strategicObjectivesLookup = [],
    strategicInitiativesLookup = [],
    operationalInitiativesLookup = [],
  } = lookups

  const templateObjectives: any[] = template?.objectives_data?.objectives || []

  if (templateObjectives.length === 0) {
    return { objectives: [], error: 'Template has no objectives to allocate' }
  }

  const objectivesWithoutKPA = templateObjectives.filter(obj => !hasAnyKpaSignal(obj))
  if (objectivesWithoutKPA.length > 0) {
    return {
      objectives: [],
      error: `Template has ${objectivesWithoutKPA.length} objective(s) without KPA assignment. Please fix the template first.`,
    }
  }

  const objectives = templateObjectives
    .map(obj => ({ ...obj, resolved_kpa_id: resolveKpaId(obj) }))
    .filter(obj => obj.resolved_kpa_id)
    .map(obj => {
      // Resolve L1 strategic objective id from code or pillar name
      let strategicObjectiveId: number | undefined
      if (obj.l1_strategic_objective_code) {
        const l1 = strategicObjectivesLookup.find(o => o.code === obj.l1_strategic_objective_code)
        if (l1) strategicObjectiveId = l1.id
      }
      if (!strategicObjectiveId && obj.pillar_name) {
        const l1 = strategicObjectivesLookup.find(
          o => o.name.toLowerCase() === obj.pillar_name.toLowerCase() ||
               (o.display_name || '').toLowerCase() === obj.pillar_name.toLowerCase()
        )
        if (l1) strategicObjectiveId = l1.id
      }

      // Resolve L2 strategic initiative id from code or name
      let strategicInitiativeId: number | undefined
      if (obj.l2_strategic_initiative_code) {
        const l2 = strategicInitiativesLookup.find(i => i.code === obj.l2_strategic_initiative_code)
        if (l2) strategicInitiativeId = l2.id
      }
      if (!strategicInitiativeId && obj.strategic_objective_name) {
        const l2 = strategicInitiativesLookup.find(
          i => i.name.toLowerCase() === obj.strategic_objective_name.toLowerCase()
        )
        if (l2) strategicInitiativeId = l2.id
      }

      // Resolve L3 operational initiative id from code or name
      let operationalInitiativeId: number | undefined
      if (obj.l3_operational_initiative_code) {
        const l3 = operationalInitiativesLookup.find(o => o.code === obj.l3_operational_initiative_code)
        if (l3) operationalInitiativeId = l3.id
      }
      if (!operationalInitiativeId && obj.l3_operational_initiative_name) {
        const l3 = operationalInitiativesLookup.find(
          o => o.name.toLowerCase() === obj.l3_operational_initiative_name.toLowerCase()
        )
        if (l3) operationalInitiativeId = l3.id
      }

      return {
        title: obj.title,
        description: obj.description,
        kpa: obj.resolved_kpa_id.toString(),
        startDate: obj.start_date?.split('T')[0],
        endDate: obj.end_date?.split('T')[0],
        strategicObjectiveId,
        strategicInitiativeId,
        operationalInitiativeId,
        kpis: (obj.kpis || []).map((kpi: any) => ({
          title: kpi.name || kpi.title,
          description: kpi.description || '',
          target: kpi.target_value?.toString() || kpi.target?.toString() || '',
          unit: kpi.unit || '',
          frequency: kpi.frequency || 'bi-annually',
        })),
      }
    })

  return { objectives }
}
